import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  createTheme,
  Stack,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { useTranslation } from 'react-i18next';

import CutImage from '../shared/img/3d/cut.png';
import GrainImage from '../shared/img/3d/grain.png';
import SeamImage from '../shared/img/3d/seam.png';
import TrimImage from '../shared/img/3d/trim.png';
import CanadaBeefLogo from '../shared/img/logo.jpg';
import Wysiwyg from './Wysiwyg';

const theme = createTheme({
  palette: {
    secondary: {
      main: grey[400],
    },
  },
});

let modelViewerScriptPromise;

const loadModelViewerScript = () => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.customElements?.get('model-viewer')) return Promise.resolve();
  if (modelViewerScriptPromise) return modelViewerScriptPromise;

  modelViewerScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'module';
    script.src = 'https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load model-viewer script'));
    document.head.appendChild(script);
  });

  return modelViewerScriptPromise;
};

const resolveAssetUrl = (fileValue) => {
  if (!fileValue) return null;

  if (typeof fileValue === 'string') {
    if (fileValue.startsWith('http://') || fileValue.startsWith('https://')) return fileValue;
    return `${process.env.REACT_APP_API_URL}/assets/${fileValue}`;
  }

  if (typeof fileValue === 'object') {
    if (fileValue?.id) return `${process.env.REACT_APP_API_URL}/assets/${fileValue.id}`;
    if (fileValue?.filename_disk) return `${process.env.REACT_APP_API_URL}/assets/${fileValue.filename_disk}`;
    if (fileValue?.url) return fileValue.url;
  }

  return null;
};

const legendItems = [
  { image: CutImage, label: 'Cut' },
  { image: GrainImage, label: 'Grain' },
  { image: SeamImage, label: 'Seam' },
  { image: TrimImage, label: 'Trim' },
];

const ModelViewer3D = ({ file, fileAnnotated, title }) => {
  const { t } = useTranslation();
  const [scriptError, setScriptError] = useState(null);
  const [showAnnotated, setShowAnnotated] = useState(Boolean(fileAnnotated));

  const plainUrl = useMemo(() => resolveAssetUrl(file), [file]);
  const annotatedUrl = useMemo(() => resolveAssetUrl(fileAnnotated), [fileAnnotated]);
  const activeSrc = showAnnotated ? (annotatedUrl || plainUrl) : (plainUrl || annotatedUrl);
  const hasBoth = Boolean(plainUrl && annotatedUrl);

  useEffect(() => {
    loadModelViewerScript().catch((error) => {
      setScriptError(error?.message || 'Unable to load model-viewer');
    });
  }, []);

  useEffect(() => {
    setShowAnnotated(Boolean(fileAnnotated));
  }, [fileAnnotated]);

  if (!plainUrl && !annotatedUrl) return null;

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-end">
        {hasBoth && (
          <ThemeProvider theme={theme}>
            <ButtonGroup
              aria-label={t('viewToggle', 'Plain or Annotated view')}
              color="secondary"
              sx={{ mb: 1 }}
            >
              <Button
                variant={!showAnnotated ? 'contained' : 'outlined'}
                onClick={() => setShowAnnotated(false)}
                sx={{ color: '#212121', textTransform: 'none', boxShadow: 'none' }}
              >
                {t('plainView', 'Plain View')}
              </Button>
              <Button
                variant={showAnnotated ? 'contained' : 'outlined'}
                onClick={() => setShowAnnotated(true)}
                sx={{ color: '#212121', textTransform: 'none', boxShadow: 'none' }}
              >
                {t('annotatedView', 'Annotated View')}
              </Button>
            </ButtonGroup>
          </ThemeProvider>
        )}

        <img src={CanadaBeefLogo} alt="Canada Beef" style={{ width: '6rem' }} />
      </Stack>

      <model-viewer
        src={activeSrc}
        camera-controls
        touch-action="pan-y"
        auto-rotate
        shadow-intensity="1"
        style={{ width: '100%', height: '450px', backgroundColor: '#f6f6f6' }}
      />

      <Typography component="div" sx={{ mt: 0.5, color: '#6a6868' }}>
        {t('threedDisclaimer')}
      </Typography>

      {showAnnotated && (
        <Stack direction="row" spacing={0} justifyContent="center" sx={{ mt: 3 }}>
          {legendItems.map((item) => (
            <Stack
              key={item.label}
              direction="column"
              alignItems="center"
              sx={{ border: '1px solid #e0e0e0' }}
            >
              <img
                className="quality-guide-image"
                src={item.image}
                alt={item.label}
                style={{ width: '5rem' }}
              />
              <Typography sx={{ fontWeight: 'bold' }} component="div">
                <Wysiwyg>{item.label}</Wysiwyg>
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {scriptError && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {scriptError}
        </Typography>
      )}
    </Box>
  );
};

export default ModelViewer3D;
